import { useState, useCallback } from 'react';
import axios from 'axios';
import { VERCEL_FUNCTIONS } from '../../config/api';

/**
 * Hook pour synchroniser les transactions Qonto
 *
 * Usage:
 * const {
 *   syncQonto,
 *   syncing,
 *   syncResult,
 *   error
 * } = useQontoSync();
 *
 * // Lancer la synchronisation
 * await syncQonto(connectionId);
 */

export function useQontoSync() {
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [error, setError] = useState(null);

  /**
   * Synchronise les transactions Qonto
   *
   * @param {string} connectionId - ID de la connexion bancaire Qonto
   * @returns {Promise<Object>} Résultat de la synchronisation
   */
  const syncQonto = useCallback(async (connectionId) => {
    setSyncing(true);
    setError(null);
    setSyncResult(null);

    try {
      const token = localStorage.getItem('auth_token');

      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      if (!connectionId) {
        throw new Error('connectionId requis');
      }

      console.log(`[Qonto Sync] Démarrage de la synchronisation pour ${connectionId}...`);

      const response = await axios.post(
        VERCEL_FUNCTIONS.QONTO_SYNC,
        { connectionId },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 35000 // 35s (API Vercel timeout à 30s)
        }
      );

      console.log('[Qonto Sync] Synchronisation terminée:', response.data);

      setSyncResult(response.data);
      return response.data;

    } catch (err) {
      console.error('[Qonto Sync] Erreur:', err);

      let errorMessage;

      if (err.code === 'ECONNABORTED') {
        errorMessage = 'La synchronisation a pris trop de temps (timeout de 30s dépassé). Veuillez réessayer.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Credentials Qonto invalides. Veuillez reconfigurer la connexion.';
      } else if (err.response?.status === 500) {
        errorMessage = err.response?.data?.message || 'Erreur serveur lors de la synchronisation';
      } else {
        errorMessage = err.response?.data?.error || err.message;
      }

      setError(errorMessage);
      throw new Error(errorMessage);

    } finally {
      setSyncing(false);
    }
  }, []);

  /**
   * Réinitialise l'état (résultat et erreur)
   */
  const resetState = useCallback(() => {
    setSyncResult(null);
    setError(null);
  }, []);

  return {
    syncQonto,
    syncing,
    syncResult,
    error,
    resetState
  };
}

export default useQontoSync;
